/** @jsxImportSource @emotion/react */
import { t as coreT } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Formik, FormikConfig, FormikProps, useFormikContext } from 'formik';
import _ from 'lodash';
import { getSportTypeLabel } from 'modules/core/domain/constants/sport-type-labels';
import { SportType } from 'modules/core/domain/enums/sport-type';
import {
  DEFAULT_MEDIA_KIND,
  MediaKind,
  VIDEO_MEDIA_KINDS,
  canvasTemplateTypeFor,
  invalidPlaceholderKeysForKind,
  isVideoMediaKind,
  mediaKindLabel,
  templateTypeFor,
} from 'modules/manager/domain/media-kind';
import {
  DEFAULT_TEMPLATE_RESOLUTION,
  TEMPLATE_RESOLUTION_LIST,
  TemplateResolutionKey,
  VIDEO_TEMPLATE_RESOLUTION,
  templateResolutionsFor,
} from 'modules/manager/domain/template-resolutions';
import { TEMPLATE_TYPE_REGISTRY, TemplateType, getTemplateTypeEntry } from 'modules/manager/domain/template-type';
import { normalizeTemplateName } from 'modules/manager/domain/template-name-conflict';
import { MediaKindCatalogItem, useMediaKindCatalogQuery } from 'modules/manager/store/media-manager-api';
import { modalButton, modalError, modalInputStyle } from 'modules/template-editor/ui/components/elements/modals/styles';
import React, { useEffect, useMemo, useRef } from 'react';
import useIsDev from 'shared/hooks/use-is-dev';
import { i18n } from 'shared/i18n';
import UDFormCheckbox from 'ud-form/components/checkbox';
import UDFormInput from 'ud-form/components/input';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import { UDModalDirtyGuard } from 'ud-form/components/modal-dirty-guard';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import * as yup from 'yup';

export type ResolutionOption = SelectOption<TemplateResolutionKey>;
export type SportTypeOption = SelectOption<SportType>;
export type MediaKindOption = SelectOption<MediaKind> & { isDisabled?: boolean };
export type SubtypeOption = SelectOption<string>;

export type MediaManagerTemplateFormValues = {
  name: string;
  resolution: ResolutionOption;
  /** `null` = any sport. */
  sportType: SportTypeOption | null;
  mediaKind: MediaKindOption;
  isDefaultForKind: boolean;
  /** Single subtype option, or `null` when the type has no subtype dimension or none picked yet. */
  subtype: SubtypeOption | null;
};

type Props = FormikConfig<MediaManagerTemplateFormValues> & {
  existingNames: string[];
  isLoading: boolean;
  submitLabel?: string;
  /** Error from the last failed submit (e.g. the DB rejected the name). */
  errorMessage?: string | null;
  /**
   * Canvas objects of the template being edited. When the new kind's canvas type
   * does not allow some of them, the form lists those keys (US-2).
   */
  canvasObjects?: unknown;
};

export const RESOLUTION_OPTIONS: ResolutionOption[] = TEMPLATE_RESOLUTION_LIST.map((r) => ({
  value: r.key,
  label: r.label,
}));

export const DEFAULT_RESOLUTION_OPTION: ResolutionOption = RESOLUTION_OPTIONS.find(
  (o) => o.value === DEFAULT_TEMPLATE_RESOLUTION.key,
)!;

export const getResolutionOption = (key: TemplateResolutionKey): ResolutionOption =>
  RESOLUTION_OPTIONS.find((o) => o.value === key) ?? DEFAULT_RESOLUTION_OPTION;

const VIDEO_RESOLUTION_OPTION = getResolutionOption(VIDEO_TEMPLATE_RESOLUTION.key);

/** The sizes offered for a kind: a video review template has exactly one (IMGM-1606). */
export const getResolutionOptionsFor = (kind: string | undefined): ResolutionOption[] =>
  templateResolutionsFor(isVideoMediaKind(kind)).map((r) => getResolutionOption(r.key));

/**
 * Video review kinds (IMGM-1606) are always selectable: they are never generated, so the backend's `supported` says
 * nothing about them. Until the backend lists them in `mediaKindCatalog`
 * (`.claude/prd/prd-match-video-template-types-backend.md`) the local dev server adds the missing ones, so the video
 * templates can be built and checked; the test stand and prod show only what the backend gives.
 */
export function withVideoMediaKinds(catalog: readonly MediaKindCatalogItem[], addMissing: boolean): MediaKindCatalogItem[] {
  const rows = catalog.map((info) => (isVideoMediaKind(info.kind) ? { ...info, supported: true } : info));
  if (!addMissing) return rows;
  const missing = VIDEO_MEDIA_KINDS.filter((kind) => !rows.some((row) => row.kind === kind)).map((kind) => ({
    kind,
    title: i18n._(mediaKindLabel(kind)),
    templateType: templateTypeFor(kind),
    supported: true,
  }));
  return [...rows, ...missing];
}

export const getSportTypeOptions = (): SportTypeOption[] =>
  Object.values(SportType).map((value) => ({
    value,
    label: i18n._(getSportTypeLabel(value)!),
  }));

export const getSportTypeOption = (sport: SportType | null | undefined): SportTypeOption | null => {
  if (!sport) return null;
  return getSportTypeOptions().find((o) => o.value === sport) ?? { value: sport, label: sport };
};

export const getMediaKindOption = (kind: MediaKind): MediaKindOption => ({
  value: kind,
  label: i18n._(mediaKindLabel(kind)),
});

export const getDefaultMediaKindOption = (): MediaKindOption => getMediaKindOption(DEFAULT_MEDIA_KIND);

export function getSubtypeOptions(type: TemplateType): readonly SubtypeOption[] {
  return getTemplateTypeEntry(type).subtypes?.options.map((o) => ({ value: o.value, label: i18n._(o.label) })) ?? [];
}

export function getSubtypeOptionForValue(type: TemplateType, value: string | undefined): SubtypeOption | null {
  if (!value) return null;
  const opt = getSubtypeOptions(type).find((o) => o.value === value);
  return opt ?? null;
}

export function buildInitialSubtype(type: TemplateType, current: readonly string[] | undefined): SubtypeOption | null {
  const cfg = getTemplateTypeEntry(type).subtypes;
  if (!cfg) return null;
  return getSubtypeOptionForValue(type, current?.[0]);
}

const validator = (existingNames: string[]) =>
  yup.object({
    name: yup
      .string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .test('media-manager-template-name', 'Invalid name', function (value) {
        if (!value || value.length <= 0) {
          return this.createError({ message: coreT`Название шаблона не может быть пустым` });
        }
        // Case-insensitive, like the DB's alias_tournament_name unique rule — a
        // stricter DB would otherwise reject a name this check just approved.
        if (existingNames.map(normalizeTemplateName).includes(normalizeTemplateName(value))) {
          return this.createError({ message: coreT`Такое название уже существует, придумайте новое` });
        }
        return true;
      }),
    resolution: yup
      .object({ value: yup.string().required(), label: yup.string().required() })
      .nullable()
      .required(() => coreT`Выберите разрешение`),
    sportType: yup.object({ value: yup.string().required(), label: yup.string().required() }).nullable(),
    mediaKind: yup
      .object({ value: yup.string().required(), label: yup.string().required() })
      .nullable()
      .required(() => coreT`Выберите тип медиа`),
    isDefaultForKind: yup.boolean(),
    subtype: yup
      .object({ value: yup.string().required(), label: yup.string().required() })
      .nullable()
      .test('subtype-required', function (value) {
        const kind = (this.parent as MediaManagerTemplateFormValues).mediaKind?.value as MediaKind | undefined;
        if (!kind) return true;
        const type = canvasTemplateTypeFor(kind);
        const cfg = TEMPLATE_TYPE_REGISTRY[type].subtypes;
        if (!cfg || !cfg.required) return true;
        if (value) return true;
        const subtypeName = i18n._(cfg.label).toLowerCase();
        return this.createError({ message: coreT`Выберите: ${subtypeName}` });
      }),
  });

export const getDefaultTemplateFormValues = (): MediaManagerTemplateFormValues => ({
  name: '',
  resolution: DEFAULT_RESOLUTION_OPTION,
  sportType: null,
  mediaKind: getDefaultMediaKindOption(),
  isDefaultForKind: false,
  subtype: null,
});

function KindFields({ canvasObjects }: { canvasObjects?: unknown }) {
  const { t } = useLingui();
  const formProps = useFormikContext<MediaManagerTemplateFormValues>();
  const sportValue = formProps.values.sportType?.value ?? null;
  const { data: backendCatalog, isFetching } = useMediaKindCatalogQuery({ sportType: sportValue });
  const isDev = useIsDev();
  // Only once the backend answered: an empty list must still mean «loading», not «only the video kinds».
  const catalog = useMemo(() => (backendCatalog ? withVideoMediaKinds(backendCatalog, isDev) : []), [backendCatalog, isDev]);

  const kindOptions: MediaKindOption[] = useMemo(
    () =>
      catalog.map((info) => ({
        value: info.kind,
        label: info.supported ? info.title : `${info.title} (${t`скоро`})`,
        isDisabled: !info.supported,
      })),
    [catalog, t],
  );

  useEffect(() => {
    const current = formProps.values.mediaKind?.value;
    if (catalog.length === 0) return;
    const match = catalog.find((info) => info.kind === current);
    if (match) {
      const option = kindOptions.find((o) => o.value === current);
      if (option && option.label !== formProps.values.mediaKind.label) {
        formProps.setFieldValue('mediaKind', option, false);
      }
      return;
    }
    const first = catalog.find((info) => info.supported) ?? catalog[0];
    const option = kindOptions.find((o) => o.value === first.kind);
    if (option) {
      formProps.setFieldValue('mediaKind', option);
      formProps.setFieldValue('subtype', null);
    }
    // Kind options and catalog are the source; form helpers are stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, kindOptions]);

  const activeKind = formProps.values.mediaKind.value as MediaKind;
  const isVideo = isVideoMediaKind(activeKind);
  const wasVideo = useRef(isVideo);
  // A video template is always the review frame size; leaving a video kind gives the image default back. An image
  // template keeps the size it was opened with — only a kind change moves it.
  const resolutionValue = formProps.values.resolution?.value;
  const { setFieldValue } = formProps;
  useEffect(() => {
    const leftVideo = wasVideo.current && !isVideo;
    wasVideo.current = isVideo;
    if (isVideo && resolutionValue !== VIDEO_RESOLUTION_OPTION.value) {
      setFieldValue('resolution', VIDEO_RESOLUTION_OPTION);
    } else if (leftVideo && resolutionValue === VIDEO_RESOLUTION_OPTION.value) {
      setFieldValue('resolution', DEFAULT_RESOLUTION_OPTION);
    }
  }, [isVideo, resolutionValue, setFieldValue]);

  const activeType = canvasTemplateTypeFor(activeKind);
  const subtypeCfg = TEMPLATE_TYPE_REGISTRY[activeType].subtypes;
  const subtypeOptions = subtypeCfg ? getSubtypeOptions(activeType) : [];
  const subtypeLabel = subtypeCfg ? t(subtypeCfg.label) : '';
  const subtypeName = subtypeLabel.toLowerCase();
  const invalidKeys = canvasObjects ? invalidPlaceholderKeysForKind(canvasObjects, activeKind) : [];

  return (
    <>
      <UDFormSelect
        name="sportType"
        label={t`Вид спорта`}
        placeholder={t`Любой вид спорта`}
        options={getSportTypeOptions()}
        isClearable
      />
      <UDFormSelect
        name="mediaKind"
        label={t`Тип медиа`}
        placeholder={t`Выберите тип`}
        options={kindOptions}
        isLoading={isFetching}
        isDisabled={isFetching && kindOptions.length === 0}
        isOptionDisabled={(option) => Boolean((option as MediaKindOption).isDisabled)}
        onChange={() => {
          formProps.setFieldValue('subtype', null);
          formProps.setFieldTouched('subtype', false, false);
        }}
      />
      {invalidKeys.length > 0 && (
        <UDText type="footnote" className="text-inkSecondary">
          {t`На холсте останутся плейсхолдеры, которых нет у нового типа:`} {invalidKeys.join(', ')}
        </UDText>
      )}
      <UDFormCheckbox name="isDefaultForKind" label={t`По умолчанию для этого типа`} />
      {subtypeCfg && (
        <UDFormSelect name="subtype" label={subtypeLabel} placeholder={t`Выберите: ${subtypeName}`} options={subtypeOptions} />
      )}
    </>
  );
}

function MediaManagerTemplateForm(props: Props) {
  const { t } = useLingui();
  const { existingNames, isLoading, submitLabel, errorMessage, canvasObjects, ...rest } = props;

  const getErrors = (formProps: FormikProps<MediaManagerTemplateFormValues>): string | undefined => {
    const error = _.find(formProps.errors, (err, key) => !!err && !!formProps.touched[key as keyof MediaManagerTemplateFormValues]);
    if (!error) return errorMessage ?? undefined;
    return typeof error === 'string' ? error : Object.values(error).find((v): v is string => typeof v === 'string');
  };

  return (
    <Formik validateOnChange validationSchema={validator(existingNames)} {...rest}>
      {(formProps) => (
        <form onSubmit={formProps.handleSubmit} className="w-full">
          <UDModalDirtyGuard />
          <Modal.Body className="flex flex-col gap-5">
            <UDFormInput
              readOnly={isLoading}
              onChangeCapture={() => {
                formProps.values.name && !formProps.touched.name && formProps.setFieldTouched('name');
              }}
              name="name"
              autoFocus
              css={modalInputStyle}
              placeholder={t`Название шаблона`}
            />
            <UDFormSelect
              name="resolution"
              label={t`Разрешение`}
              placeholder={t`Выберите разрешение`}
              options={getResolutionOptionsFor(formProps.values.mediaKind?.value)}
            />
            <KindFields canvasObjects={canvasObjects} />
          </Modal.Body>
          <Modal.Footer justify="start">
            {/* Замок — от Formik: `onSubmit` модалки возвращает промис (IMGM-1561). */}
            <UDButton
              type="submit"
              variant="primary"
              className="flex-center"
              disabled={!formProps.isValid || formProps.values.name.trim() === ''}
              css={modalButton}
            >
              {submitLabel ?? t`Создать`}
            </UDButton>
            <span css={modalError}>{getErrors(formProps)}</span>
          </Modal.Footer>
        </form>
      )}
    </Formik>
  );
}

export default MediaManagerTemplateForm;
